import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const publicApplicationsService = {
  /**
   * Get all applications with filters
   * @param {Object} filters - { status, job_id, search, city, nationality, from_date, to_date, page, per_page }
   */
  getApplications: async (filters = {}) => {
    try {
      const response = await apiClient.get('/public-applications', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw error;
    }
  },

  /**
   * Get single application by ID with all relations
   * @param {number} id - Application ID
   */
  getApplication: async (id) => {
    try {
      const response = await apiClient.get(`/public-applications/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching application:', error);
      throw error;
    }
  },

  /**
   * Get application statistics
   */
  getStats: async () => {
    try {
      const response = await apiClient.get('/public-applications/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  },

  /**
   * Update application status
   * @param {number} id - Application ID
   * @param {string} status - New status
   * @param {string} candidate_id - Optional candidate ID if moving to candidate
   */
  updateStatus: async (id, status, candidate_id = null) => {
    try {
      const response = await apiClient.patch(`/public-applications/${id}/status`, {
        status,
        candidate_id,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating status:', error);
      throw error;
    }
  },

  /**
   * Add note to application
   * @param {number} id - Application ID
   * @param {string} note_text - Note content
   * @param {boolean} is_private - Privacy flag
   */
  addNote: async (id, note_text, is_private = false) => {
    try {
      const response = await apiClient.post(`/public-applications/${id}/notes`, {
        note_text,
        is_private,
      });
      return response.data;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  },

  /**
   * Add evaluation to application
   * @param {number} id - Application ID
   * @param {Object} evaluation - Evaluation data
   */
  addEvaluation: async (id, evaluation) => {
    try {
      const response = await apiClient.post(`/public-applications/${id}/evaluations`, evaluation);
      return response.data;
    } catch (error) {
      console.error('Error adding evaluation:', error);
      throw error;
    }
  },

  /**
   * Get CV signed URL
   * @param {number} id - Application ID
   */
  getCVUrl: async (id) => {
    try {
      const response = await apiClient.get(`/public-applications/${id}/cv-url`);
      return response.data;
    } catch (error) {
      console.error('Error getting CV URL:', error);
      throw error;
    }
  },

  /**
   * Get profile picture signed URL
   * @param {number} id - Application ID
   */
  getProfilePictureUrl: async (id) => {
    try {
      const response = await apiClient.get(`/public-applications/${id}/profile-picture-url`);
      return response.data;
    } catch (error) {
      console.error('Error getting profile picture URL:', error);
      throw error;
    }
  },

  /**
   * Get available export columns
   */
  getExportColumns: async () => {
    try {
      const response = await apiClient.get('/public-applications/export-columns');
      return response.data;
    } catch (error) {
      console.error('Error getting export columns:', error);
      throw error;
    }
  },

  /**
   * Export applications to Excel
   * @param {Object} params - Export parameters (filters, columns, etc.)
   */
  exportApplications: async (params = {}) => {
    try {
      const response = await apiClient.post('/public-applications/export', params, {
        responseType: 'blob', // Important for file download
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'applications_export.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'File downloaded successfully' };
    } catch (error) {
      console.error('Error exporting applications:', error);
      throw error;
    }
  },
};

export default publicApplicationsService;
